// src/components/TestimonialsSection.jsx
import React, { useState, useEffect, useRef } from "react";
import { Box, Typography, Avatar, Container } from "@mui/material";
import { DATA } from "../../pages/ProjectsPage/ProjectsData";

function TestimonialsSection() {
	// Function to get first image from project by client name
	const getClientProjectImage = (clientName) => {
		const project = DATA.find(
			(p) =>
				p.client && p.client.toLowerCase().includes(clientName.toLowerCase())
		);
		return project && project.images && project.images.length > 0
			? project.images[0]
			: "/images/Testimonials_placeholder_image.png";
	};

	const testimonials = [
		{
			text: `Saffron House turned our vision into reality with incredible clarity and ease. The project was delivered exactly on time and without any hassle. Every detail reflects our personality, we couldn't be happier with the result.`,
			authorName: "Yogesh Kaushal",
			image: "/images/testimonials/Yogesh_Kaushal .png",
		},
		{
			text: `What truly sets Saffron House apart is their ability to create something utterly unique. The way they worked with colors is exceptional; the palette has added a depth and quiet luxury that has completely transformed the atmosphere of our office. It feels sophisticated yet warm, bold yet beautifully balanced.`,
			authorName: "Vaibhav Maheshwari",
			image: "/images/testimonials/Vaibhav_Maheshwari.jpeg",
		},
		{
			text: `Muskan brought a new perspective to our demo flat and office. She understood the brief quickly and shaped the space to feel inviting, and luxurious. Her clarity, design sensibility, and attention to detail made the entire process seamless.`,
			authorName: "Sahil Kandhari",
			image: "/images/testimonials/Sahil_Kandhari.jpg",
		},
		{
			text: `Muskan transformed our apartment into a space that feels grand and breathtaking, exactly what we dreamed of. We wanted a European-inspired aesthetic with elegant whites and timeless detailing, and they delivered it flawlessly. Every corner feels refined and immersive, like stepping into a beautifully curated European apartment. It's everything we hoped for.`,
			authorName: "Dr. Madan Sodani",
			image: "/images/testimonials/Dr._Madan_Sodani.jpeg",
		},
	];

	// Add project image to each testimonial
	const testimonialsWithProjectImages = testimonials.map((item) => ({
		...item,
		projectImage: getClientProjectImage(item.authorName),
	}));

	const [currentIndex, setCurrentIndex] = useState(0);
	const intervalRef = useRef(null);
	const totalSlides = testimonialsWithProjectImages.length;

	const startInterval = () => {
		clearInterval(intervalRef.current);
		intervalRef.current = setInterval(() => {
			setCurrentIndex((prev) => (prev + 1) % totalSlides);
		}, 5000);
	};

	useEffect(() => {
		startInterval();
		return () => clearInterval(intervalRef.current);
	}, [currentIndex]);

	const goToSlide = (index) => setCurrentIndex(index);

	return (
		<Container
			maxWidth={false}
			sx={{
				width: { xs: "95%", lg: "min(1500px, 95%)" },
				px: 0,
				my: { xs: 6, md: 8 },
			}}
		>
			{/* Header row (desktop only dots) */}
			<Box
				sx={{
					display: "flex",
					justifyContent: "space-between",
					alignItems: "flex-start",
					gap: 2,
					mb: { xs: 2, md: 4 },
				}}
			>
				<Box>
					<Typography
						variant="h2"
						sx={{
							fontFamily: '"Lora", serif',
							fontWeight: 500,
							fontSize: { xs: 32, sm: 36, md: 44 },
							lineHeight: { xs: "38px", md: "50px" },
							color: "#674934",
							textAlign: "left",
						}}
					>
						Testimonials
					</Typography>

					<Typography
						sx={{
							fontFamily: '"Source Sans Pro", sans-serif',
							fontWeight: 400,
							fontSize: { xs: 15, md: 18 },
							lineHeight: "22px",
							color: "#674934",
							textAlign: "left",
							mt: 0.2,
						}}
					>
						Voices from Our Transformed Spaces
					</Typography>
				</Box>

				{/* DESKTOP dots only */}
				{/* <Box
					sx={{
						display: { xs: "none", md: "flex" },
						gap: 1,
						mt: 2,
					}}
				>
					{testimonials.map((_, index) => (
						<Box
							key={index}
							onClick={() => goToSlide(index)}
							sx={{
								width: 14,
								height: 14,
								borderRadius: "50%",
								bgcolor: "#674934",
								opacity: index === currentIndex ? 1 : 0.4,
								cursor: "pointer",
								transition: "opacity 0.3s ease",
							}}
						/>
					))}
				</Box> */}
			</Box>

			{/* Slider container */}
			<Box sx={{ width: "100%", overflow: "hidden" }}>
				<Box
					onMouseEnter={() => clearInterval(intervalRef.current)}
					onMouseLeave={startInterval}
					sx={{
						display: "flex",
						width: "100%",
						transition: "transform 0.5s ease-in-out",
						transform: `translateX(-${currentIndex * 100}%)`,
					}}
				>
					{testimonialsWithProjectImages.map((item, index) => (
						<Box
							key={index}
							sx={{
								minWidth: "100%",
								width: "100%",
								flex: "0 0 100%",
								boxSizing: "border-box",
								display: "flex",
								flexDirection: { xs: "column", md: "row" },
								alignItems: "center",
								gap: { xs: 2, md: 4 },
							}}
						>
							{/* Image */}
							<Box
								component="img"
								src={item.projectImage}
								alt={`${item.authorName} Project`}
								sx={{
									width: { xs: "100%", md: "46%" },
									height: { xs: 210, sm: 250, md: 390 },
									borderRadius: 2,
									objectFit: "cover",
									boxShadow: "0 4px 16px rgba(0,0,0,.1)",
								}}
							/>

							{/* Text + Author */}
							<Box
								sx={{
									flex: 1,
									maxWidth: { md: "50%" },

									// LEFT ALIGN in mobile
									textAlign: "left",

									mt: { xs: 2, md: 0 },
								}}
							>
								<Typography
									sx={{
										fontFamily: '"Lora", serif',
										fontSize: { xs: 17, sm: 18, md: 24 },
										fontWeight: 400,
										fontStyle: "italic",
										color: "#674934",
										lineHeight: { xs: "28px", md: "40px" },
										mb: 2,
									}}
								>
									{item.text}
								</Typography>

								{/* Author */}
								<Box
									sx={{
										display: "flex",
										alignItems: "center",
										justifyContent: "flex-start",
										gap: 1.5,
										mb: 2,
									}}
								>
									<Typography
										sx={{
											fontFamily: '"Source Sans Pro", sans-serif',
											fontSize: 22,
											color: "#674934",
										}}
									>
										—
									</Typography>

									<Avatar
										src={item.image}
										alt={item.authorName}
										sx={{ width: 48, height: 48 }}
									/>

									<Typography
										sx={{
											fontFamily: '"Source Sans Pro", sans-serif',
											fontSize: 20,
											color: "#674934",
										}}
									>
										{item.authorName}
									</Typography>
								</Box>
							</Box>
						</Box>
					))}
				</Box>
			</Box>
		</Container>
	);
}

export default TestimonialsSection;
